import React, { useRef } from "react";

import { DropTargetMonitor, useDrop } from "react-dnd";
import type { XYCoord } from "dnd-core";

import Condition, { enum_conditional } from "../../models/conditions/Condition";
import ConditionRule from "./ConditionRule";
import ConditionGroup from "./ConditionGroup";
import ConditionReference from "./ConditionReference";
import { draggable_types, ConditionDragItem } from "../../helpers/DragHelper";
import { SxProps } from "@mui/material";
import { useRowSelector, useTableSelector } from "../../store/Hooks";

// Shared set of props for all types of conditions
// If editingFunctions is undefined, that is equivalent to creating a "readonly" condition
export interface ConditionProps {
  condition_id: number;
  index: number;
  readonly: boolean;
  sx?: SxProps;
}

enum condition_types {
  group = "group",
  rule = "rule",
  reference = "reference",
}

const determineConditionType = (condition: Condition): condition_types => {
  if (
    condition.conditional === enum_conditional.and ||
    condition.conditional === enum_conditional.or
  ) {
    return condition_types.group;
  } else if (condition.conditional === enum_conditional.condition) {
    return condition_types.reference;
  } else {
    return condition_types.rule;
  }
};

const ConditionComponent = (props: ConditionProps) => {
  const condition = useRowSelector(Condition, props.condition_id);
  const allConditions = useTableSelector(Condition);
  if (typeof condition === "undefined") {
    console.error(allConditions);
    throw new Error(
      "Unable to display a condition that does not exist: " +
        props.condition_id,
    );
  }
  const parent = useRowSelector(Condition, condition.parent_condition_id as number);
  const type = determineConditionType(condition as Condition);
  const readonly = props.readonly;
  const ref = useRef<HTMLDivElement>(null);

  const [{ draggedItem }, drop] = useDrop<
    ConditionDragItem,
    void,
    { draggedItem: ConditionDragItem | null }
  >(
    () => ({
      accept: draggable_types.Condition,
      canDrop: () => true,
      hover: (item: ConditionDragItem, monitor: DropTargetMonitor) => {
        // Make sure we're not hovering over a nested component
        // Check if we're "readonly", in which case we can't edit the tree
        // Make sure the ref exists as well
        if (!monitor.isOver({ shallow: true }) || readonly || !ref.current) {
          return;
        }

        // Figure out if the object is over the top half or bottom half
        const hoverBoundingRect = ref.current?.getBoundingClientRect();
        const clientOffset = monitor.getClientOffset();
        const hoverClientY =
          (clientOffset as XYCoord).y - hoverBoundingRect.top;
        let overTop,
          overBot = false;
        if (
          hoverClientY <
          (hoverBoundingRect.bottom - hoverBoundingRect.top) / 10
        ) {
          overTop = true;
        }
        if (
          hoverClientY >
          ((hoverBoundingRect.bottom - hoverBoundingRect.top) / 10) * 9
        ) {
          overBot = true;
        }

        if (overTop || overBot) {
          // If a different condition is hovering over this condition, then we want to put it above or below this condition
          // That might entail completely changing the parent of the hovering condition, or just moving it in the children array
          const newIndex = overTop ? props.index : props.index + 1;
          if (item.parentID === condition.parent_condition_id ) {
            // The hovering condition already has the correct parent, so we just need to move it within the parent

            // Don't update if index is already correct
            if (item.index === newIndex) {
              return;
            }

            // Check if item is a root condition i.e. parent = null (it shouldn't be, but still good to check)
            if (item.parentID !== null) {
              Condition.moveCondition(
                item.conditionID,
                item.parentID,
                newIndex,
              );
              // Update item manually, otherwise this will keep firing
              item.index = newIndex;
            }
          } else if (item.parentID !== null && condition.parent_condition_id  !== null) {
            // The hovering condition does not have the correct parent, so we need to remove it from its current parent
            // while also inserting it into the new parent at the correct position
            Condition.moveCondition(
              item.conditionID,
              condition.parent_condition_id ,
              newIndex,
              item.parentID,
            );
            // Update item manually, otherwise this will keep firing
            item.index = newIndex;
            item.parentID = condition.parent_condition_id;
          }
        }
      },
      collect: (monitor: DropTargetMonitor) => ({
        draggedItem: monitor.getItem(),
      }),
    }),
    [condition],
  );

  let isDragging = false;
  if (
    draggedItem instanceof ConditionDragItem &&
    draggedItem.conditionID === condition.id
  ) {
    isDragging = true;
  }

  const sx = {
    ...props.sx,
    opacity: isDragging ? 0.5 : 1,
  };

  drop(ref);
  if (type === condition_types.group) {
    return <ConditionGroup ref={ref} sx={sx} {...props}></ConditionGroup>;
  } else if (
    type === condition_types.reference &&
    typeof parent !== "undefined"
  ) {
    return <ConditionReference ref={ref} sx={sx} {...props} />;
  } else if (type === condition_types.rule && typeof parent !== "undefined") {
    return <ConditionRule ref={ref} sx={sx} {...props} />;
  } else {
    return <></>;
  }
};

export default ConditionComponent;
